// lib/leasing/sourceBudget.ts

export const OBSERVED_BRANDS_PER_CREDIT = 29 / 50;

export const resolveSourceBudgetMax = (
  globalMax: number,
  scope: 'asset' | 'portfolio',
  eligibleCount: number | null,
): number => {
  if (scope !== 'asset' || eligibleCount == null || !Number.isFinite(eligibleCount) || eligibleCount <= 0) {
    return globalMax;
  }
  return Math.min(globalMax, eligibleCount * 3);
};

/**
 * What researching one brand costs on average (every field re-checked, ten stores verified, a
 * contact search when it has none). A planning estimate only: research is billed on its metered
 * cost, this just decides how many brands a budget can carry.
 */
// Measured on the preview, 2026-09-27: 1.15 credits a brand with the store checks' provider
// unavailable; a working Places key adds about 1.6 (ten checks). Rounded up to keep a margin.
export const ESTIMATED_RESEARCH_CREDITS_PER_BRAND = 3;

/** How many brands a budget can carry through the run: scoring alone, or research then scoring. */
export const brandsForCredits = (credits: number, withResearch = false): number => {
  const perBrand = 1 / OBSERVED_BRANDS_PER_CREDIT + (withResearch ? ESTIMATED_RESEARCH_CREDITS_PER_BRAND : 0);
  return Math.max(0, Math.floor(Math.max(0, credits) / perBrand));
};

/** Credits needed to score `brandCount` brands at the measured rate, plus their research when on. */
export const creditsForBrands = (brandCount: number, withResearch = false): number =>
  Math.max(1, Math.ceil(brandCount * (1 / OBSERVED_BRANDS_PER_CREDIT + (withResearch ? ESTIMATED_RESEARCH_CREDITS_PER_BRAND : 0))));

export const estimateSourceBrandCount = (budgetCredits: number, assetCount = 1): number =>
  Math.max(0, Math.round((budgetCredits * OBSERVED_BRANDS_PER_CREDIT) / Math.max(1, assetCount)));

export const describeSourceBudget = (
  language: string,
  budgetCredits: number,
  assetCount: number,
  /** Every pass researches its brands, so the budget carries fewer than scoring alone would. */
  withResearch = true,
): string => {
  const estimatedBrands = withResearch
    ? brandsForCredits(budgetCredits / Math.max(1, assetCount), true)
    : estimateSourceBrandCount(budgetCredits, assetCount);
  const researchNote = withResearch
    ? language === 'fr' ? ' avec la recherche' : ' with research'
    : '';
  const sharedPrefix =
    assetCount > 1
      ? language === 'fr'
        ? `Plafond de dépense partagé entre ${assetCount} actifs. `
        : `Spending ceiling shared across ${assetCount} assets. `
      : language === 'fr'
        ? 'Plafond de dépense. '
        : 'Spending ceiling. ';
  const observedRatio = language === 'fr' ? '0,58' : '0.58';

  if (language === 'fr') {
    return `${sharedPrefix}Repère observé : 29 marques / 50 crédits (≈ ${observedRatio} marque/crédit). Cela représente environ ${estimatedBrands} marque${estimatedBrands > 1 ? 's' : ''}${assetCount > 1 ? ' par actif' : ''}${researchNote}, à titre indicatif et non garanti. Le résultat réel varie selon le coût de l'analyse.`;
  }
  return `${sharedPrefix}Observed reference: 29 brands / 50 credits (≈ ${observedRatio} brand/credit). This is an indicative, non-guaranteed estimate of about ${estimatedBrands} brand${estimatedBrands === 1 ? '' : 's'}${assetCount > 1 ? ' per asset' : ''}${researchNote}. Actual results vary with analysis cost.`;
};

export const resolvePersistedSourcingBudget = (
  requestedBudgetCredits: number,
  selectedTargetBrandCount: number,
  assetCount: number,
): number =>
  assetCount === 1 && requestedBudgetCredits > selectedTargetBrandCount
    ? requestedBudgetCredits
    : selectedTargetBrandCount;
